import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

const HlsPlayer = ({
  src,
  isVisible,
  isMuted,
  playing,
  onEnded,
  onPlay,
  onPause,
  poster,
  id,
  style,
  videoRef: externalVideoRef
}) => {
  const internalVideoRef = useRef(null);
  const videoRef = externalVideoRef || internalVideoRef;
  const [ready, setReady] = useState(false);
  const [buffering, setBuffering] = useState(false);

  const isIOS = typeof navigator !== 'undefined' && /iP(hone|od|ad)/.test(navigator.userAgent);

  useEffect(() => {
  const video = videoRef.current;
    if (!video || !src) return;

    let hls;
    if (Hls.isSupported()) {
      hls = new Hls({
        maxBufferLength: 30,
        maxBufferHole: 1,
        fragLoadingTimeOut: 20000,
        enableWorker: true,
      });
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });

      // const onError = (event, data) => {
      //   console.debug('hls error', data);
      //   if (data && data.type === Hls.ErrorTypes.MEDIA_ERROR) {
      //     try { hls.recoverMediaError(); } catch (e) { console.debug(e); }
      //   }
      //   if (data && data.details === Hls.ErrorDetails.FRAG_PARSING_ERROR) {
      //     setTimeout(() => { try { hls.startLoad(); } catch (e) { console.debug(e); } }, 500);
      //   }
      // };

      // hls.on(Hls.Events.ERROR, onError);
      video._hls = hls;
    } else {
      video.src = src;
    }

    const handleLoaded = () => {
      setReady(true);
      setBuffering(false);
    };
    const handlePlaying = () => {
      setReady(true);
      setBuffering(false);
    };
    const handleWaiting = () => {
      setBuffering(true);
    };
    const handleStalled = () => {
      setBuffering(true);
    };

    video.addEventListener('loadeddata', handleLoaded);
    video.addEventListener('playing', handlePlaying);

    if(!isIOS) {
      video.addEventListener('waiting', handleWaiting);
      video.addEventListener('stalled', handleStalled);
    }

    return () => {
      video.removeEventListener('loadeddata', handleLoaded);
      video.removeEventListener('playing', handlePlaying);

      if(!isIOS) {
        video.removeEventListener('waiting', handleWaiting);
        video.removeEventListener('stalled', handleStalled);
      }
      
      try {
        if (hls) { hls.destroy(); }
        if (video && video._hls) { video._hls.destroy(); delete video._hls; }
      } catch (e) { console.debug('destroy error', e); }
    };
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !!isMuted;
  }, [isMuted]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (playing && isVisible) {
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      try { video.pause(); } catch (e) {}
    }
  }, [playing, isVisible]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (ready && playing && isVisible) {
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, [ready, playing, isVisible]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => onEnded && onEnded();
    const handlePlay = () => { onPlay && onPlay(); setReady(true); };
    const handlePause = () => { onPause && onPause(); };
    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [onEnded, onPlay, onPause]);

  return (
    <div id={`hls-container-${id || ''}`} style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          backgroundImage: (buffering || !ready) && poster ? `url(${poster})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: (buffering || !ready) && !poster ? '#000' : undefined,
        }}
        playsInline
        controls={false}
      />
      {buffering && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3,
            pointerEvents: 'none',
            background: 'rgba(0,0,0,0.25)'
          }}
        >
          <svg width="48" height="48" viewBox="0 0 50 50" aria-hidden="true">
            <circle cx="25" cy="25" r="20" fill="none" stroke="#ffffff" strokeWidth="4" strokeOpacity="0.25" />
            <circle cx="25" cy="25" r="20" fill="none" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" strokeDasharray="31.4 31.4">
              <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
            </circle>
          </svg>
        </div>
      )}
      {!ready && poster && (
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${poster})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 2 }} />
      )}
      {!ready && !poster && (
        <div style={{ position: 'absolute', inset: 0, background: '#000', zIndex: 2 }} />
      )}
    </div>
  );
};

export default HlsPlayer;
